import { fetchStoreData } from '@/apis/bujianStore'
import {
  AwaitStoreImageSize,
  DEFAULT_SHOW_AWAIT_STORE_IMAGE,
  From,
} from '@/data/constant'
import { getPlankMatCode } from '@/util/plankCommonFuncs'
import { getBujianItemKey } from '@/views/newPaiban/util/bujianData'
import { nanoid } from 'nanoid'

import { StoreSourceStrategy } from '../config/storeSourceStrategy'

export interface IFetchStorePlankDataParams {
  colorTextureThickStr?: string
  filterWord?: string
}

export async function fetchStorePlankDataByStrategy(
  sourceStrategy: StoreSourceStrategy,
  { colorTextureThickStr = '', filterWord = '' }: IFetchStorePlankDataParams
) {
  const res = await fetchStoreData({
    filter_type: sourceStrategy.filterType,
    library_type: sourceStrategy.libraryType,
    color_texture_thick_str: colorTextureThickStr,
    filter_word: filterWord,
  })
  if (res.status !== 1) return []
  return res.data?.data ?? []
}

export async function normalizeStorePlankDataByStrategy(
  sourceStrategy: StoreSourceStrategy,
  data: any[]
) {
  const newData = await Promise.all(
    data.map(async (item) => {
      const { render_url, plate_info, create_time, id, img_url } = item
      const imgUrl = img_url || DEFAULT_SHOW_AWAIT_STORE_IMAGE
      const plank = JSON.parse(plate_info)

      const { partUniqueId, thick, matCode, color, width, height, address } =
        plank
      plank.partUniqueId = partUniqueId || nanoid()
      plank.width = width || `${AwaitStoreImageSize.width}px`
      plank.height = height || `${AwaitStoreImageSize.height}`
      plank.matCode = getPlankMatCode(plank)
      const realThick = thick ?? plank.size?.split('*').slice(-1)[0] ?? 0
      plank.title = `${realThick}${matCode}(${color})`
      plank.thick = Number(realThick)
      return {
        ...plank,
        bujianItemKey:
          sourceStrategy.key === 'bujianStore'
            ? getBujianItemKey({
                id,
                partUniqueId: plank.partUniqueId,
              })
            : '',
        url: imgUrl,
        render_url,
        id,
        create_time: create_time,
        from: plank.from ?? From.LBL,
        isDone: sourceStrategy.initialDone ?? 'load',
        type: sourceStrategy.itemType ?? 'serve',
        oriData: item,
        address: address || item.address,
      }
    })
  )
  return newData
}

export async function fetchAndNormalizeStorePlankDataByStrategy(
  sourceStrategy: StoreSourceStrategy,
  params: IFetchStorePlankDataParams
) {
  const storePlankData = await fetchStorePlankDataByStrategy(
    sourceStrategy,
    params
  )
  return normalizeStorePlankDataByStrategy(sourceStrategy, storePlankData)
}

export function useStorePlankData(sourceStrategy: StoreSourceStrategy) {
  async function fetchStorePlankData(params: IFetchStorePlankDataParams) {
    return fetchStorePlankDataByStrategy(sourceStrategy, params)
  }

  async function normalizeStorePlankData(data: any[]) {
    return normalizeStorePlankDataByStrategy(sourceStrategy, data)
  }

  async function fetchAndNormalizeStorePlankData(
    params: IFetchStorePlankDataParams
  ) {
    return fetchAndNormalizeStorePlankDataByStrategy(sourceStrategy, params)
  }

  return {
    fetchAndNormalizeStorePlankData,
    fetchStorePlankData,
    normalizeStorePlankData,
  }
}
